import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import {
  type NotificationSubscriptionDataType,
  mutateNotificationDelete,
  mutateNotificationUnsubscribe,
} from '@/api/settings';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { useMe } from '@/providers/User';

type Props = {
  subscription: NotificationSubscriptionDataType | null;
  onCancel: () => void;
  onDeleteComplete: (success: boolean, isSubscriptionOwner: boolean) => void;
};

const DeleteNotificationSubscriptionDialog = ({
  subscription,
  onCancel,
  onDeleteComplete,
}: Props) => {
  const currentUser = useMe();
  const [isDeleting, setIsDeleting] = useState(false);
  const isSubscriptionOwner = subscription?.createdBy.id === currentUser?.id;

  const handleDelete = async () => {
    setIsDeleting(true);

    let success = false;

    try {
      if (isSubscriptionOwner) {
        success = await mutateNotificationDelete(subscription!.id);
      } else {
        success = await mutateNotificationUnsubscribe(subscription!.id);
      }
    } catch {
      success = false;
    }

    onDeleteComplete(isSubscriptionOwner, success);
    setIsDeleting(false);
  };

  const additionalRecipients = subscription?.additionalRecipients ?? [];

  return (
    <Dialog
      open={subscription != null}
      onOpenChange={(isOpen) => {
        if (isDeleting) return;
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent showCloseButton={!isDeleting}>
        <DialogHeader>
          <DialogTitle>
            <Trans>Delete Notification</Trans>
          </DialogTitle>
        </DialogHeader>
        <div>
          {isSubscriptionOwner ? (
            <div>
              <p>
                {additionalRecipients.length ? (
                  <Trans>
                    Deleting this notification will end delivery for you and the following
                    recipients. Do you wish to proceed?
                  </Trans>
                ) : (
                  <Trans>
                    Deleting this notification will end delivery for you. Do you wish to proceed?
                  </Trans>
                )}
              </p>
              <p>
                <b>{additionalRecipients.map((subscriber) => subscriber.email).join(', ')}</b>
              </p>
            </div>
          ) : (
            <Trans>
              You are not the owner of this notification. Unsubscribing will only end notifications
              to you.
            </Trans>
          )}
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large" disabled={isDeleting}>
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          {isSubscriptionOwner ? (
            <Button
              size="large"
              variant="primary"
              danger
              isLoading={isDeleting}
              onClick={handleDelete}
            >
              <Trans>Yes, Delete</Trans>
            </Button>
          ) : (
            <Button size="large" variant="on-primary" isLoading={isDeleting} onClick={handleDelete}>
              <Trans>Unsubscribe</Trans>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteNotificationSubscriptionDialog;
